import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Avatar, EmptyState, ErrorState, Spinner } from "@/components/common";
import { CallButton, useCallsAvailable } from "@/components/call/CallButton";
import { ReportButton } from "@/components/ReportButton";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const timeOf = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const ConversationHeading = ({ t, meId }) => {
  const callsOn = useCallsAvailable();
  const other = t.kind === "direct" ? t.participants[0] : null;
  const others = t.members.filter((m) => m.id !== meId);
  return (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {other && <Link to={`/members/${other.id}`} aria-label={`View ${other.name}'s profile`} data-testid="conversation-avatar-link"><Avatar person={other} size={44} /></Link>}
        <div className="min-w-0">
          {other ? <Link to={`/members/${other.id}`} className="font-display block truncate text-2xl hover:text-primary" data-testid="conversation-title">{t.title}</Link>
            : <p className="font-display truncate text-2xl" data-testid="conversation-title">{t.title}</p>}
          <p className="truncate text-xs text-muted-foreground" data-testid="conversation-members">
            {t.kind === "experience" ? <>Private circle · {others.map((m, i) => <span key={m.id}>{i > 0 && ", "}<Link to={`/members/${m.id}`} className="hover:text-primary hover:underline" data-testid={`conversation-member-${m.id}`}>{m.name}</Link></span>)}</> : "Private conversation"}
          </p>
        </div>
      </div>
      {other && callsOn && t.connected_ids?.includes(other.id) && (
        <div className="flex gap-2" data-testid="conversation-call-actions">
          <CallButton member={other} compact voice />
          <CallButton member={other} compact />
        </div>
      )}
    </>
  );
};

const ThreadList = ({ threads, activeId }) => (
  <ul className="divide-y divide-border" data-testid="thread-list">
    {threads.map((t) => (
      <li key={t.id}>
        <Link to={`/messages/${t.id}`} data-testid={`thread-${t.id}`} className={cn("flex min-h-[72px] items-center gap-3 px-4 py-3 transition-colors hover:bg-secondary/60", t.id === activeId && "bg-secondary")}>
          <Avatar person={t.kind === "direct" ? t.participants[0] : { name: t.title }} size={44} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className={cn("truncate", t.unread && "font-semibold")}>{t.title}</p>
              {t.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
            </div>
            <p className="truncate text-sm text-muted-foreground">{t.kind === "experience" ? "Circle · " : ""}{t.last_message || "No messages yet"}</p>
          </div>
        </Link>
      </li>
    ))}
  </ul>
);

const Conversation = ({ id, onSent }) => {
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/threads/${id}`); setT(data); setError(""); } catch (e) { setError(apiError(e)); }
  }, [id]);
  useEffect(() => { setT(null); load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [t?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/threads/${id}/messages`, { body });
      setT((p) => ({ ...p, messages: [...p.messages, data] }));
      setBody("");
      onSent();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (error) return <div className="p-6"><ErrorState message={error} /></div>;
  if (!t) return <Spinner />;
  return (
    <div className="flex h-full flex-col" data-testid="conversation">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Link to="/messages" className="flex h-11 w-11 items-center justify-center lg:hidden" aria-label="Back to conversations" data-testid="conversation-back"><ArrowLeft className="h-5 w-5" /></Link>
        <ConversationHeading t={t} meId={user.id} />
        {t.experience_id && <Link to={`/experiences/${t.experience_id}`} className="btn-link text-sm" data-testid="conversation-experience-link">View plan</Link>}
      </header>
      <ol className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="message-list" aria-live="polite">
        {t.messages.length === 0 && <li className="py-10 text-center text-sm text-muted-foreground">Start the conversation. Messages are visible only to participants.</li>}
        {t.messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <li key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")} data-testid={`message-${m.id}`}>
              <div className={cn("max-w-[80%] rounded-2xl px-4 py-3", mine ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-secondary")}>
                {!mine && <p className="mb-1 text-xs font-semibold text-primary">{m.sender_name}</p>}
                <p className={cn("whitespace-pre-line break-words", m.removed && "italic opacity-70")} data-testid={`message-body-${m.id}`}>{m.body}</p>
                {!mine && !m.removed && <ReportButton kind="message" target={m.id} compact className="mt-1" testId={`report-message-${m.id}`} />}
                <p className={cn("mt-1 text-[11px]", mine ? "text-primary-foreground/70" : "text-muted-foreground")}>{timeOf(m.created_at)}</p>
              </div>
            </li>
          );
        })}
        <li ref={end} aria-hidden />
      </ol>
      <form onSubmit={send} className="flex items-end gap-3 border-t border-border p-3">
        <label htmlFor="msg" className="sr-only">Message</label>
        <textarea id="msg" rows={1} maxLength={2000} className="field max-h-40 min-h-[48px] flex-1 resize-none py-3" placeholder="Write a message" value={body}
          onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} data-testid="message-input" />
        <button type="submit" className="btn-primary h-12 w-12 min-h-0 px-0" disabled={busy || !body.trim()} aria-label="Send message" data-testid="send-message-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
};

export default function Messages() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/threads").then(({ data }) => setThreads(data)).catch((e) => setError(apiError(e))), []);
  useEffect(() => { load(); }, [load, threadId]);

  return (
    <div className="shell pb-6 pt-6 lg:pt-10" data-testid="messages-page">
      <h1 className={cn("font-display mb-6 text-5xl leading-none", threadId && "hidden lg:block")}>Messages</h1>
      {error ? <ErrorState message={error} /> : !threads ? <Spinner /> : threads.length === 0 && !threadId ? (
        <EmptyState title="No conversations yet." body="Message a member from their profile, or join an experience to talk with its circle." testId="messages-empty"
          action={<button type="button" className="btn-outline" onClick={() => navigate("/circle")} data-testid="messages-find-members">Find members</button>} />
      ) : (
        <div className="surface grid h-[calc(100dvh-210px)] min-h-[420px] overflow-hidden lg:h-[calc(100vh-220px)] lg:grid-cols-[340px_1fr]">
          <div className={cn("overflow-y-auto border-r border-border", threadId && "hidden lg:block")}><ThreadList threads={threads} activeId={threadId} /></div>
          <div className={cn("min-h-0", !threadId && "hidden lg:block")}>
            {threadId ? <Conversation key={threadId} id={threadId} onSent={load} /> : <div className="flex h-full items-center justify-center text-muted-foreground">Choose a conversation</div>}
          </div>
        </div>
      )}
    </div>
  );
}
